import Lottie from 'lottie-react';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NewsletterSubscribeForm } from 'src/components';
import Logo from 'src/images/logos/logo.svg?react';
import * as CookieConsent from 'vanilla-cookieconsent';

import cartAnimation from 'src/images/lotties/footer-carts.json';

export const Footer = () => {
  const footerLinksContRef = useRef<HTMLDivElement>(null);
  const footerLinksOneRef = useRef<HTMLLIElement>(null);
  const footerLinksTwoRef = useRef<HTMLLIElement>(null);

  const { t, i18n } = useTranslation();

  const i18nLang = i18n.resolvedLanguage
    ? i18n.resolvedLanguage.toLowerCase()
    : i18n.language.toLowerCase();

  useLayoutEffect(() => {
    if (!footerLinksContRef.current || !footerLinksOneRef.current || !footerLinksTwoRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      const footerLinksHeight = footerLinksContRef.current!.clientHeight;
      const flic1Height = footerLinksOneRef.current!.clientHeight;
      const flic1Width = footerLinksOneRef.current!.clientWidth;
      const flic2Height = footerLinksTwoRef.current!.clientHeight;
      const flic2Width = footerLinksTwoRef.current!.clientWidth;
      const tallestFooterLinks = Math.max(flic1Height, flic2Height);
      // if the height of the footer links container is larger than the tallest footer link
      // then we know the links are still stacked on top of each other, meaning we should
      // make sure they're the same width
      if (footerLinksHeight > tallestFooterLinks) {
        if (flic1Width > flic2Width) {
          footerLinksTwoRef.current!.style.width = `${flic1Width}px`;
        } else if (flic2Width > flic1Width) {
          footerLinksOneRef.current!.style.width = `${flic2Width}px`;
        }
      }
    });
    resizeObserver.observe(footerLinksContRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  return (
    <div className='flex shrink-0 flex-col items-center justify-center gap-6 bg-white pt-8 font-secondary text-base font-semibold text-primary-dark sm:p-12 lg:p-20 lg:pt-12'>
      <span className='p-2 text-center font-secondary text-[13px] font-normal text-blueGray'>
        {t('footer.affiliate_disclaimer')}
      </span>
      <Link
        to={`/`}
        reloadDocument
        className='mb-4 flex flex-row px-1 py-1 transition-all active:scale-[97%]'
      >
        <Logo title='Share-A-Cart Logo' />
      </Link>
      <div className='flex w-full flex-col flex-wrap items-center justify-center gap-y-12 p-8 pt-0 xs:flex-row md:flex-row'>
        <nav
          ref={footerLinksContRef}
          className={`flex w-full flex-col flex-wrap items-start justify-evenly gap-12 md:gap-10 lg:flex-nowrap lg:gap-14 xl:mr-auto xl:w-auto ${i18nLang === 'ru' ? 'sm:flex-row' : 'xs:flex-row'}`}
        >
          <li ref={footerLinksOneRef} className='flex flex-col justify-center gap-y-6'>
            <h4 className='heading-200 px-1'>{t('footer.shopping_tools')}</h4>
            <Link
              to={`/make`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('common.create_a_cart')}
            </Link>
            <Link
              to='/get'
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('common.receive_a_cart')}
            </Link>
            <Link
              to='/deals'
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('deals.all_link')}
            </Link>
            <Link
              to='/deals/latest'
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('deals.latest_link')}
            </Link>
            <Link
              to='/deals/expiring-soon'
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('deals.expiring_soon_link')}
            </Link>
            <Link
              to='/deals/promo-codes'
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('deals.promo_codes_link')}
            </Link>
            <Link
              to={`/supported`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.supported_stores')}
            </Link>
            <Link
              to={`/plugins`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.plugins')}
            </Link>
            <Link
              to={`/plus`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.get_sac_plus')}
            </Link>
            <h4 className='heading-200 px-1 pt-4'>{t('footer.company')}</h4>
            <Link
              to={`/about`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.about')}
            </Link>
            <Link
              to={`/business-solutions`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.business_solutions')}
            </Link>
            <Link
              to='/developers'
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.developers')}
            </Link>
            <Link
              to={`/about#contactform`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.contact_us')}
            </Link>
          </li>
          <li ref={footerLinksTwoRef} className='flex flex-col justify-center gap-y-6'>
            <h4 className='heading-200 px-1'>{t('footer.resources')}</h4>
            <Link
              to={`/compare`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.why_choose_share_a_cart')}
            </Link>
            <Link
              to={`/blog`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.blog')}
            </Link>
            <Link
              to={`/guide`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.how_to_guides')}
            </Link>
            <Link
              to={`/faq`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.faqs')}
            </Link>
            <Link
              to={`/solutions`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('navbar.solutions')}
            </Link>
            <Link
              to='/changelog'
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.changelog')}
            </Link>
            <h4 className='heading-200 px-1 pt-4'>{t('footer.legal')}</h4>
            <button
              type='button'
              onClick={() => {
                CookieConsent.showPreferences();
              }}
              className='font-inherit max-w-max cursor-pointer border-none bg-transparent px-1 text-left leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              Manage Cookies
            </button>
            <Link
              to={`/privacy`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.privacy_policy')}
            </Link>
            <Link
              to={`/terms-of-service`}
              reloadDocument
              className='max-w-max px-1 leading-[25.6px] transition-all hover:text-primary active:scale-[97%]'
            >
              {t('footer.terms_of_service')}
            </Link>
          </li>
        </nav>

        <div className='mb-auto flex w-fit flex-col items-start justify-center font-normal'>
          <span className='text-200 w-full text-pretty text-center md:max-w-[440px] md:text-left'>
            {t('footer.subscribe_to_newsletter')}:
          </span>
          <NewsletterSubscribeForm />
        </div>
      </div>

      <div className='flex w-full flex-col items-center'>
        <Lottie
          animationData={cartAnimation}
          style={{ width: '100%', maxWidth: '400px', height: 'auto' }}
        />
        <div className='h-1 w-full border-t border-black'></div>
        <span className='mt-1 w-full text-left text-sm font-normal'>© 2026, Share-A-Cart</span>
      </div>
    </div>
  );
};
